import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import ExcelDownloadButton from "../../BulkEdit/GetExcelSample";
import { TaxonomyPreflightHint } from "../../BulkEdit/TaxonomyErrors";
import { useBulkStageUpload, PHASES } from "../../BulkEdit/useBulkStageUpload";
import {
  formatError,
  getErrorField,
  getFieldLabel,
  getTaxonomyDeepLink,
} from "../../BulkEdit/bulkStageErrors";
import { exportFailedRowsCsv, exportUnstagedRowsCsv } from "../../BulkEdit/exportFailedRowsCsv";
import "../../BulkEdit/BulkEditStyle.css";

const ALLOWED_EXTENSIONS = /\.(xlsx|xls|csv)$/i;
const ALLOWED_MIME_TYPES = [
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
  "text/csv",
  "application/csv",
];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const ProductBulkUpload = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [filePickError, setFilePickError] = useState("");
  const { state, lastResult, actions } = useBulkStageUpload();

  const handleFileChange = (e) => {
    setFilePickError("");
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) {
      setFile(null);
      return;
    }
    if (
      !ALLOWED_MIME_TYPES.includes(selectedFile.type) &&
      !ALLOWED_EXTENSIONS.test(selectedFile.name)
    ) {
      setFilePickError("Only .xlsx, .xls, or .csv files are allowed.");
      e.target.value = "";
      setFile(null);
      return;
    }
    if (selectedFile.size > MAX_FILE_SIZE) {
      setFilePickError("File size exceeds 10MB limit.");
      e.target.value = "";
      setFile(null);
      return;
    }
    setFile(selectedFile);
    actions.reset();
  };

  const handleUploadClick = () => {
    if (!file) {
      setFilePickError("Please select a file first.");
      return;
    }
    actions.startUpload(file);
  };

  const handleStartOver = () => {
    setFile(null);
    setFilePickError("");
    actions.reset();
    // Clear the native file input so the same file can be re-picked.
    const input = document.getElementById("excel-file");
    if (input) input.value = "";
  };

  const isBusy =
    state.phase === PHASES.PARSING ||
    state.phase === PHASES.UPLOADING ||
    state.phase === PHASES.STREAMING;

  const handleDownloadCurrentFailures = () => {
    exportFailedRowsCsv({
      rowFailures: state.rowFailures,
      parsedRows: state.parsedRows,
      fileName: state.file?.name,
    });
  };

  const handleDownloadCurrentUnstaged = () => {
    exportUnstagedRowsCsv({
      rowFailures: state.rowFailures,
      parsedRows: state.parsedRows,
      processed: state.processed,
      fileName: state.file?.name,
    });
  };

  const handleDownloadPersistedFailures = () => {
    if (!lastResult) return;
    exportFailedRowsCsv({
      rowFailures: lastResult.rowFailures,
      parsedRows: lastResult.parsedRows,
      fileName: lastResult.fileName,
    });
  };

  // Show the resume banner when we're on a fresh idle screen AND there's a
  // persisted result from a previous upload. Once the user resumes (state
  // hydrates) or runs a new upload (overwrites lastResult), this naturally
  // hides itself.
  const showResumeBanner =
    state.phase === PHASES.IDLE &&
    lastResult &&
    Array.isArray(lastResult.rowFailures) &&
    lastResult.rowFailures.length > 0;

  return (
    <div className="product-bulk-upload">
      {showResumeBanner && (
        <ResumeBanner
          lastResult={lastResult}
          onResume={actions.restoreLastResult}
          onDownload={handleDownloadPersistedFailures}
          onDismiss={actions.dismissLastResult}
        />
      )}

      {/* SECTION 1: FILE UPLOAD */}
      <div className="container my-4 p-4 border rounded bg-light">
        <div className="card-header d-flex justify-content-between align-items-center mb-3">
          <h3 style={{ fontWeight: 500, fontSize: 22 }}>
            <i className="bi bi-upload me-2"></i>
            Excel / CSV File Upload
          </h3>
          <div className="d-flex gap-2">
            <button
              className="btn btn-outline-primary"
              onClick={() => navigate("/product/pending")}
            >
              View Pending Products
            </button>
            <ExcelDownloadButton />
          </div>
        </div>

        <div className="card-body">
          <TaxonomyPreflightHint />

          <div className="row align-items-end">
            <div className="col-md-8">
              <label htmlFor="excel-file" className="form-label">
                Select file
              </label>
              <input
                id="excel-file"
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileChange}
                className="form-control"
                disabled={isBusy}
              />
              {file && (
                <div className="form-text">
                  Selected: <strong>{file.name}</strong> ({(file.size / 1024).toFixed(1)} KB)
                </div>
              )}
              {filePickError && (
                <div className="text-danger small mt-2">{filePickError}</div>
              )}
            </div>
            <div className="col-md-4 d-flex justify-content-between">
              <button
                className="btn btn-primary w-100"
                onClick={handleUploadClick}
                disabled={!file || isBusy}
              >
                {state.phase === PHASES.PARSING && (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" />
                    Reading file…
                  </>
                )}
                {state.phase === PHASES.UPLOADING && (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" />
                    Uploading…
                  </>
                )}
                {state.phase === PHASES.STREAMING && (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" />
                    Validating {state.processed} of {state.total}…
                  </>
                )}
                {!isBusy && (
                  <>
                    <i className="bi bi-upload me-2"></i>
                    Upload
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* SECTION 2: PROGRESS / RESULTS */}

      {state.phase === PHASES.PARSING && <PreparingCard fileName={state.file?.name} />}

      {(state.phase === PHASES.STREAMING || state.phase === PHASES.UPLOADING) && (
        <UploadProgressCard state={state} onCancel={actions.cancel} />
      )}

      {state.phase === PHASES.DONE_SUCCESS && (
        <SuccessCard
          summary={state.summary}
          onViewLive={() => navigate("/product")}
          onViewPending={() => navigate("/product/pending")}
          onUploadAnother={handleStartOver}
        />
      )}

      {state.phase === PHASES.DONE_WITH_ERRORS && (
        <PartialResultCard
          state={state}
          onViewLive={() => navigate("/product")}
          onViewPending={() => navigate("/product/pending")}
          onUploadAnother={handleStartOver}
          onDownloadFailedRows={handleDownloadCurrentFailures}
        />
      )}

      {state.phase === PHASES.INCOMPLETE && (
        <IncompleteResultCard
          state={state}
          onViewLive={() => navigate("/product")}
          onViewPending={() => navigate("/product/pending")}
          onUploadAnother={handleStartOver}
          onDownloadFailedRows={handleDownloadCurrentFailures}
          onDownloadUnstaged={handleDownloadCurrentUnstaged}
        />
      )}

      {state.phase === PHASES.CLIENT_ERROR && (
        <FailureListCard
          title="Fix these issues, then re-pick the file"
          subtitle="The file can't be uploaded yet. None of these errors hit the server — they're all things we can detect locally."
          rowFailures={state.rowFailures}
          onPrimary={handleStartOver}
          primaryLabel="Pick a new file"
          onDownloadFailedRows={handleDownloadCurrentFailures}
        />
      )}

      {state.phase === PHASES.CANCELLED && (
        <InfoCard
          icon="bi-stop-circle"
          tone="warning"
          title="Upload cancelled"
          message={
            <>
              Some rows may have already landed before you cancelled.{" "}
              <Link to="/product/pending">Open Pending Products</Link> or{" "}
              <Link to="/product">Open Product List</Link> to see what made it through.
            </>
          }
          primaryLabel="Start over"
          onPrimary={handleStartOver}
        />
      )}

      {state.phase === PHASES.TRANSPORT_ERROR && (
        <RequestErrorCard
          requestError={state.requestError}
          transportError={state.transportError}
          onRetry={handleStartOver}
        />
      )}
    </div>
  );
};

// --- Sub-components -------------------------------------------------------

// Inline pill row for the three dispatcher outcomes. Renders all three buckets
// even when zero so the user sees the full breakdown at a glance.
function BucketPills({ live, staged, rejected, size = "md" }) {
  const cls = size === "sm" ? "small" : "";
  return (
    <div className={`d-flex flex-wrap gap-3 ${cls}`}>
      <span>
        <span className="text-success fw-semibold">{live}</span>{" "}
        <span className="text-muted">live</span>
      </span>
      <span>
        <span className="text-primary fw-semibold">{staged}</span>{" "}
        <span className="text-muted">pending review</span>
      </span>
      <span>
        <span className="text-danger fw-semibold">{rejected}</span>{" "}
        <span className="text-muted">rejected</span>
      </span>
    </div>
  );
}

// Shown while we're parsing the file locally — before the upload starts.
// Indeterminate animation since we don't have row-level progress for parsing.
function PreparingCard({ fileName }) {
  return (
    <div className="container my-4 p-4 border rounded bg-white">
      <div className="d-flex align-items-center gap-3 mb-3">
        <span className="spinner-border text-primary" role="status" aria-hidden="true" />
        <div>
          <h5 className="mb-1">Reading your file…</h5>
          <p className="text-muted small mb-0">
            {fileName ? `Parsing ${fileName}` : "Parsing rows from the file"}
          </p>
        </div>
      </div>
      <div className="progress" style={{ height: 8 }}>
        <div
          className="progress-bar progress-bar-striped progress-bar-animated bg-primary"
          role="progressbar"
          style={{ width: "100%" }}
          aria-valuenow={100}
          aria-valuemin={0}
          aria-valuemax={100}
        />
      </div>
    </div>
  );
}

// Computes elapsed / rows-per-second / ETA for the streaming card.
// Re-renders the parent on a 500ms tick so the time figures stay live without
// every reducer dispatch needing to carry a timestamp.
function useStreamMetrics(state) {
  const [, forceTick] = useState(0);
  const startedAt = state.streamStartedAt;
  useEffect(() => {
    if (!startedAt || state.phase !== PHASES.STREAMING) return undefined;
    const id = setInterval(() => forceTick((t) => t + 1), 500);
    return () => clearInterval(id);
  }, [startedAt, state.phase]);

  if (!startedAt) return { elapsedSec: 0, rowsPerSec: null, etaSec: null };
  const elapsedSec = (Date.now() - startedAt) / 1000;
  // Need a few processed rows + a beat of elapsed time before rate/ETA mean
  // anything. Until then we just show elapsed.
  if (state.processed < 2 || elapsedSec < 1) {
    return { elapsedSec, rowsPerSec: null, etaSec: null };
  }
  const rowsPerSec = state.processed / elapsedSec;
  const remaining = Math.max(state.total - state.processed, 0);
  const etaSec = rowsPerSec > 0 ? remaining / rowsPerSec : null;
  return { elapsedSec, rowsPerSec, etaSec };
}

function formatDuration(sec) {
  if (sec == null || !Number.isFinite(sec) || sec < 0) return "—";
  if (sec < 60) return `${Math.round(sec)}s`;
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

function UploadProgressCard({ state, onCancel }) {
  const pct = state.total > 0 ? Math.round((state.processed / state.total) * 100) : 0;
  const isConnecting = state.phase === PHASES.UPLOADING;
  const { elapsedSec, rowsPerSec, etaSec } = useStreamMetrics(state);

  return (
    <div className="container my-4 p-4 border rounded bg-white">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">
          <i
            className={`bi me-2 text-primary ${
              isConnecting ? "bi-cloud-arrow-up" : "bi-arrow-repeat"
            }`}
          />
          {isConnecting
            ? "Connecting to the server…"
            : `Processing ${state.processed} of ${state.total}…`}
        </h5>
        <button className="btn btn-outline-secondary btn-sm" onClick={onCancel}>
          <i className="bi bi-x-lg me-1" />
          Cancel
        </button>
      </div>

      <div className="progress" style={{ height: 14 }}>
        <div
          className={`progress-bar bg-primary ${
            isConnecting ? "progress-bar-striped progress-bar-animated" : ""
          }`}
          role="progressbar"
          style={{
            width: isConnecting ? "100%" : `${pct}%`,
            transition: isConnecting ? "none" : "width 0.3s ease-out",
            fontWeight: 600,
            fontSize: 12,
          }}
          aria-valuenow={isConnecting ? undefined : pct}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          {!isConnecting && pct >= 8 ? `${pct}%` : null}
        </div>
      </div>

      {isConnecting ? (
        <p className="small text-muted mt-2 mb-0">
          Setting up the upload — this usually takes a moment.
        </p>
      ) : (
        <div className="d-flex justify-content-between align-items-center mt-2 small text-muted flex-wrap gap-2">
          <BucketPills
            live={state.live}
            staged={state.staged}
            rejected={state.rejected}
            size="sm"
          />
          <div className="d-flex flex-wrap gap-3">
            {rowsPerSec != null && (
              <span>
                <strong className="text-dark">{rowsPerSec.toFixed(1)}</strong> rows/sec
              </span>
            )}
            {etaSec != null && etaSec > 0 && (
              <span>
                ETA <strong className="text-dark">{formatDuration(etaSec)}</strong>
              </span>
            )}
            <span>{formatDuration(elapsedSec)} elapsed</span>
          </div>
        </div>
      )}

      {state.rowFailures.length > 0 && (
        <details className="mt-3">
          <summary className="small text-muted" style={{ cursor: "pointer" }}>
            {state.rowFailures.length} row(s) rejected so far — click to see details
          </summary>
          <div className="mt-2">
            <RowFailureList rowFailures={state.rowFailures} />
          </div>
        </details>
      )}
    </div>
  );
}

// Headline copy varies by which buckets are non-zero. We don't show a separate
// "all live" path versus "all staged" path — the BucketPills always render the
// full breakdown and the headline summarizes the meaningful split.
function describeOutcome({ live, staged, rejected, total }) {
  if (rejected === 0 && live > 0 && staged === 0) {
    return {
      title: `${live} of ${total} added to the live catalog.`,
      body: "All rows had complete pricing and went straight live.",
    };
  }
  if (rejected === 0 && live === 0 && staged > 0) {
    return {
      title: `${staged} of ${total} sent for manager review.`,
      body: "Review them in Pending Products before they go live.",
    };
  }
  if (rejected === 0 && live > 0 && staged > 0) {
    return {
      title: `${live} live · ${staged} sent for review.`,
      body: "Complete rows went live; the rest need manager review in Pending Products before they go live.",
    };
  }
  // Mixed with rejections.
  return {
    title: `${live + staged} of ${total} saved · ${rejected} rejected.`,
    body: "Live rows are already in the catalog. Pending rows need manager review. Fix the rejected rows in your file and re-upload only those.",
  };
}

function SuccessCard({ summary, onViewLive, onViewPending, onUploadAnother }) {
  const live = summary?.live ?? 0;
  const staged = summary?.staged ?? 0;
  const total = summary?.total ?? live + staged;
  const { title, body } = describeOutcome({ live, staged, rejected: 0, total });
  return (
    <div className="container my-4 p-4 border rounded" style={{ background: "#f0fdf4" }}>
      <div className="d-flex align-items-start gap-3">
        <Avatar bg="#16a34a" icon="bi-check-lg" />
        <div className="flex-grow-1">
          <h5 className="mb-1">{title}</h5>
          <p className="text-muted mb-3">{body}</p>
          <BucketPills live={live} staged={staged} rejected={0} />
          <div className="d-flex flex-wrap gap-2 mt-3">
            {live > 0 && (
              <button className="btn btn-success" onClick={onViewLive}>
                <i className="bi bi-arrow-right me-1" />
                View live products
              </button>
            )}
            {staged > 0 && (
              <button
                className={`btn ${live > 0 ? "btn-outline-success" : "btn-success"}`}
                onClick={onViewPending}
              >
                <i className="bi bi-arrow-right me-1" />
                Review pending products
              </button>
            )}
            <button className="btn btn-outline-secondary" onClick={onUploadAnother}>
              Upload another file
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PartialResultCard({
  state,
  onViewLive,
  onViewPending,
  onUploadAnother,
  onDownloadFailedRows,
}) {
  const summary = state.summary || {};
  const live = summary.live ?? state.live;
  const staged = summary.staged ?? state.staged;
  const rejected = summary.rejected ?? state.rejected;
  const total = summary.total ?? state.total;
  const { title, body } = describeOutcome({ live, staged, rejected, total });
  const canDownload =
    Array.isArray(state.parsedRows) && state.parsedRows.length > 0 && state.rowFailures.length > 0;
  return (
    <>
      <div className="container my-4 p-4 border rounded" style={{ background: "#fffbeb" }}>
        <div className="d-flex align-items-start gap-3">
          <Avatar bg="#f59e0b" icon="bi-exclamation-triangle-fill" />
          <div className="flex-grow-1">
            <h5 className="mb-1">{title}</h5>
            <p className="text-muted mb-3">{body}</p>
            <BucketPills live={live} staged={staged} rejected={rejected} />
            <div className="d-flex flex-wrap gap-2 mt-3">
              {live > 0 && (
                <button className="btn btn-success" onClick={onViewLive}>
                  <i className="bi bi-arrow-right me-1" />
                  View live products
                </button>
              )}
              {staged > 0 && (
                <button className="btn btn-warning" onClick={onViewPending}>
                  <i className="bi bi-arrow-right me-1" />
                  Review pending products
                </button>
              )}
              {canDownload && (
                <button className="btn btn-outline-warning" onClick={onDownloadFailedRows}>
                  <i className="bi bi-download me-1" />
                  Download rejected rows (CSV)
                </button>
              )}
              <button className="btn btn-outline-secondary" onClick={onUploadAnother}>
                Upload a corrected file
              </button>
            </div>
          </div>
        </div>
      </div>

      <FailureListCard
        title={`${state.rowFailures.length} row(s) rejected`}
        subtitle={null}
        rowFailures={state.rowFailures}
      />
    </>
  );
}

function IncompleteResultCard({
  state,
  onViewLive,
  onViewPending,
  onUploadAnother,
  onDownloadFailedRows,
  onDownloadUnstaged,
}) {
  const total = state.total || (state.parsedRows ? state.parsedRows.length : 0);
  const processed = state.processed || 0;
  const live = state.live || 0;
  const staged = state.staged || 0;
  const rejected = state.rejected || 0;
  const notProcessed = Math.max(total - processed, 0);
  const reasonCopy =
    state.incompleteReason === "inactivity-timeout"
      ? "The server stopped responding for 60 seconds, so the upload was aborted mid-stream."
      : "The server closed the connection before sending a final summary — likely an unhandled error mid-upload.";
  const canDownloadFailed =
    Array.isArray(state.parsedRows) && state.rowFailures.length > 0;
  const canDownloadUnstaged =
    Array.isArray(state.parsedRows) && state.parsedRows.length > 0 && (notProcessed > 0 || state.rowFailures.length > 0);
  return (
    <>
      <div className="container my-4 p-4 border rounded" style={{ background: "#fffbeb" }}>
        <div className="d-flex align-items-start gap-3">
          <Avatar bg="#f59e0b" icon="bi-exclamation-triangle-fill" />
          <div className="flex-grow-1">
            <h5 className="mb-1">Upload didn't finish</h5>
            <p className="text-muted mb-2">{reasonCopy}</p>
            <div className="d-flex flex-wrap gap-3 small mb-3">
              <BucketPills live={live} staged={staged} rejected={rejected} size="sm" />
              <span>
                <span className="text-warning fw-semibold">{notProcessed}</span>{" "}
                <span className="text-muted">not processed</span>
              </span>
              <span className="text-muted">· of {total} total</span>
            </div>
            <p className="text-muted small mb-3">
              The <strong>live</strong> rows are already in the catalog and the{" "}
              <strong>pending review</strong> rows are in Pending Products.
              The <strong>rejected</strong> and <strong>not-processed</strong> rows did not land —
              fix any data issues and re-upload them after the underlying server error is resolved.
            </p>
            <div className="d-flex flex-wrap gap-2">
              {live > 0 && (
                <button className="btn btn-success" onClick={onViewLive}>
                  <i className="bi bi-arrow-right me-1" />
                  View live products
                </button>
              )}
              {staged > 0 && (
                <button className="btn btn-warning" onClick={onViewPending}>
                  <i className="bi bi-arrow-right me-1" />
                  Review pending products
                </button>
              )}
              {canDownloadUnstaged && (
                <button className="btn btn-outline-warning" onClick={onDownloadUnstaged}>
                  <i className="bi bi-download me-1" />
                  Download unsaved rows (CSV)
                </button>
              )}
              {canDownloadFailed && (
                <button className="btn btn-outline-secondary" onClick={onDownloadFailedRows}>
                  <i className="bi bi-download me-1" />
                  Download rejected rows only
                </button>
              )}
              <button className="btn btn-outline-secondary" onClick={onUploadAnother}>
                Try again
              </button>
            </div>
          </div>
        </div>
      </div>

      {state.rowFailures.length > 0 && (
        <FailureListCard
          title={`${state.rowFailures.length} row(s) explicitly rejected`}
          subtitle="These rows returned errors before the stream ended."
          rowFailures={state.rowFailures}
        />
      )}
    </>
  );
}

function FailureListCard({
  title,
  subtitle,
  rowFailures,
  onPrimary,
  primaryLabel,
  onDownloadFailedRows,
}) {
  if (!rowFailures || rowFailures.length === 0) return null;
  return (
    <div className="container my-4 p-4 border rounded bg-white">
      <h5 className="mb-1">
        <i className="bi bi-exclamation-circle text-danger me-2" />
        {title}
      </h5>
      {subtitle && <p className="text-muted small mb-3">{subtitle}</p>}
      <RowFailureList rowFailures={rowFailures} />
      {(onPrimary || onDownloadFailedRows) && (
        <div className="mt-3 d-flex flex-wrap gap-2">
          {onPrimary && (
            <button className="btn btn-primary" onClick={onPrimary}>
              {primaryLabel}
            </button>
          )}
          {onDownloadFailedRows && (
            <button className="btn btn-outline-secondary" onClick={onDownloadFailedRows}>
              <i className="bi bi-download me-1" />
              Download rejected rows (CSV)
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function ResumeBanner({ lastResult, onResume, onDownload, onDismiss }) {
  const rejectedCount = lastResult.rejected || lastResult.rowFailures?.length || 0;
  const fileName = lastResult.fileName || "previous upload";
  const liveCount = lastResult.summary?.live ?? lastResult.live ?? 0;
  const stagedCount = lastResult.summary?.staged ?? lastResult.staged ?? 0;
  const canDownload =
    Array.isArray(lastResult.parsedRows) &&
    lastResult.parsedRows.length > 0 &&
    rejectedCount > 0;
  return (
    <div
      className="container my-3 p-3 border rounded"
      style={{ background: "#fffbeb", borderColor: "#fcd34d" }}
    >
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
        <div>
          <div className="fw-semibold">
            <i className="bi bi-clock-history me-2 text-warning" />
            Last upload: {fileName}
          </div>
          <div className="small text-muted">
            {formatTimeAgo(lastResult.savedAt)}
            {" · "}
            <span className="text-danger fw-semibold">{rejectedCount} rejected</span>
            {liveCount > 0 && <> · {liveCount} live</>}
            {stagedCount > 0 && <> · {stagedCount} pending</>}
          </div>
        </div>
        <div className="d-flex flex-wrap gap-2">
          <button className="btn btn-sm btn-warning" onClick={onResume}>
            <i className="bi bi-eye me-1" />
            View errors
          </button>
          {canDownload && (
            <button className="btn btn-sm btn-outline-secondary" onClick={onDownload}>
              <i className="bi bi-download me-1" />
              Download rejected rows
            </button>
          )}
          <button className="btn btn-sm btn-link text-secondary" onClick={onDismiss}>
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
}

function formatTimeAgo(isoString) {
  if (!isoString) return "";
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return "";
  const diffSec = Math.round((Date.now() - date.getTime()) / 1000);
  if (diffSec < 60) return "just now";
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hour${diffHr > 1 ? "s" : ""} ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 7) return `${diffDay} day${diffDay > 1 ? "s" : ""} ago`;
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function RowFailureList({ rowFailures }) {
  return (
    <ul className="list-unstyled mb-0">
      {rowFailures.map((failure, idx) => (
        <li
          key={`${failure.rowIndex}-${idx}`}
          className="border rounded p-3 mb-2"
          style={{ background: "#fef2f2", borderColor: "#fca5a5" }}
        >
          <div className="d-flex justify-content-between flex-wrap mb-2">
            <strong>
              Row {failure.rowIndex + 1}
              {failure.name && ` — ${failure.name}`}
            </strong>
            {failure.barcode && (
              <span className="small text-muted">Barcode: {failure.barcode}</span>
            )}
          </div>
          <ul className="list-unstyled mb-0">
            {failure.errors.map((error, eIdx) => (
              <ErrorRow key={eIdx} error={error} />
            ))}
          </ul>
        </li>
      ))}
    </ul>
  );
}

function ErrorRow({ error }) {
  const field = getErrorField(error);
  const fieldLabel = getFieldLabel(field);
  const deepLink = getTaxonomyDeepLink(error.code);
  return (
    <li className="d-flex flex-wrap align-items-baseline gap-2 mb-1">
      {fieldLabel && (
        <span
          className="badge"
          style={{ background: "#fee2e2", color: "#991b1b", fontWeight: 500 }}
        >
          {fieldLabel}
        </span>
      )}
      <span className="text-break">{formatError(error)}</span>
      {deepLink && (
        <Link to={deepLink.route} target="_blank" rel="noreferrer" className="small ms-1">
          Open {deepLink.label} <i className="bi bi-box-arrow-up-right ms-1" />
        </Link>
      )}
    </li>
  );
}

function InfoCard({ icon, tone, title, message, primaryLabel, onPrimary }) {
  const bg = tone === "warning" ? "#fffbeb" : "#f1f5f9";
  const accent = tone === "warning" ? "#f59e0b" : "#64748b";
  return (
    <div className="container my-4 p-4 border rounded" style={{ background: bg }}>
      <div className="d-flex align-items-start gap-3">
        <Avatar bg={accent} icon={icon} />
        <div className="flex-grow-1">
          <h5 className="mb-1">{title}</h5>
          <div className="text-muted mb-3">{message}</div>
          {primaryLabel && onPrimary && (
            <button className="btn btn-primary" onClick={onPrimary}>
              {primaryLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function RequestErrorCard({ requestError, transportError, onRetry }) {
  // requestError = HTTP 400 envelope with a structured code
  // transportError = network/parse failure with a free-form string
  const title = requestError?.code
    ? requestError.code === "ERR_TOO_MANY_ROWS"
      ? "File has too many rows"
      : "The server rejected the upload"
    : "Couldn't reach the server";
  const detail = requestError ? formatError(requestError) : transportError || "Unknown error.";

  return (
    <div className="container my-4 p-4 border rounded" style={{ background: "#fef2f2" }}>
      <div className="d-flex align-items-start gap-3">
        <Avatar bg="#dc2626" icon="bi-x-circle-fill" />
        <div className="flex-grow-1">
          <h5 className="mb-1">{title}</h5>
          <p className="mb-2">{detail}</p>
          {requestError?.code && (
            <div className="small text-muted mb-3">
              Error code: <code>{requestError.code}</code>
            </div>
          )}
          <button className="btn btn-primary" onClick={onRetry}>
            Try again
          </button>
        </div>
      </div>
    </div>
  );
}

// Small circular icon shared by all status cards. Extracted to remove the
// repeated 30-line inline-styled div from each card body.
function Avatar({ bg, icon }) {
  return (
    <div
      style={{
        width: 40,
        height: 40,
        borderRadius: "50%",
        background: bg,
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 20,
        flexShrink: 0,
      }}
    >
      <i className={`bi ${icon}`} />
    </div>
  );
}

export default ProductBulkUpload;
